/**
 * Bridges project diagnostics into CodeMirror's lint system.
 *
 * The app already parses in its own debounced pipeline, so the linter must not
 * parse again — it reads whatever the app last pushed into a state field.
 * Diagnostics therefore arrive slightly stale by construction, which is why
 * every range is clamped to the current document: a diagnostic computed against
 * a longer document must not throw when the user has since deleted text.
 */
import { StateEffect, StateField } from '@codemirror/state';
import type { Extension, Text } from '@codemirror/state';
import { linter, lintGutter } from '@codemirror/lint';
import type { Diagnostic as CmDiagnostic } from '@codemirror/lint';
import type { EditorView } from '@codemirror/view';
import type { Diagnostic } from '../language/diagnostics/types';

export const setDiagnosticsEffect = StateEffect.define<readonly Diagnostic[]>();

export const diagnosticsField = StateField.define<readonly Diagnostic[]>({
  create: () => [],
  update(value, transaction) {
    for (const effect of transaction.effects) {
      if (effect.is(setDiagnosticsEffect)) return effect.value;
    }
    return value;
  },
});

export function toCodeMirrorDiagnostics(
  diagnostics: readonly Diagnostic[],
  doc: Text,
): CmDiagnostic[] {
  const limit = doc.length;
  const out: CmDiagnostic[] = [];

  for (const diagnostic of diagnostics) {
    const from = Math.max(0, Math.min(diagnostic.range.start.offset, limit));
    let to = Math.max(from, Math.min(diagnostic.range.end.offset, limit));
    // A zero-width range highlights nothing; widen it by one character so the
    // underline is visible, unless we are already at the very end.
    if (to === from) to = Math.min(from + 1, limit);

    out.push({
      from,
      to,
      severity: diagnostic.severity,
      message: diagnostic.suggestion
        ? `${diagnostic.message}\n${diagnostic.suggestion}`
        : diagnostic.message,
      source: diagnostic.code,
    });
  }

  return out;
}

/**
 * The linter itself: a pure projection of the state field, with no delay, since
 * the debounce already happened upstream.
 */
export function flowScriptLinter(): Extension {
  return [
    diagnosticsField,
    linter(
      (view: EditorView) =>
        toCodeMirrorDiagnostics(view.state.field(diagnosticsField, false) ?? [], view.state.doc),
      {
        delay: 0,
        needsRefresh: (update) =>
          update.transactions.some((t) => t.effects.some((e) => e.is(setDiagnosticsEffect))),
      },
    ),
    lintGutter(),
  ];
}
